import {MutableRefObject} from 'react';

import {IModalOpenerComponentRef} from 'components/modal-opener-component';

import {fetchFilesHashes, sendSignedFile} from '../api';
import {ISendSignedFileArgs} from '../api/types';
import {EFileEncoding, SIGNING_MESSAGE_KEY} from '../model/constants';
import {TCertificateExtended, TCryptoProSignatureType} from '../model/types';
import {notify} from './notify';
import {signContent} from './sign-content';

interface IProcessDocumentsArgs {
  documentIdsToSign?: string[];
  attachmentIdsToSign?: string[];
  entityName: string;
  certificateThumbprint: string;
  certificateCadescomAlgorithm: number;
  selectedCertificate: TCertificateExtended;
  selectedSignatureType: TCryptoProSignatureType;
  modalRef: MutableRefObject<IModalOpenerComponentRef | null>;
  selectedFileExtension?: string;
  selectedFileEncoding?: EFileEncoding;
  onStart?: () => void;
  onFinish?: () => void;
  handleSignedFileUpload?: (args: ISendSignedFileArgs) => void;
}

/**
 * Функция выполняет последовательность шагов, необходимых для подписания документов:
 * - подписывает каждый документ;
 * - отправляет подписанные файлы на сервер;
 * - уведомляет пользователя о статусе процедуры подписания.
 *
 * @param documentIdsToSign Массив идентификаторов документов, которые нужно подписать.
 * @param attachmentIdsToSign Массив идентификаторов вложений, которые нужно подписать.
 * @param entityName Название сущности, с которой связаны документы.
 * @param certificateThumbprint Отпечаток сертификата, который используется для подписания.
 * @param certificateCadescomAlgorithm Алгоритм, используемый для подписи (например, SHA-1).
 * @param selectedCertificate Выбранный сертификат, который будет использоваться для подписания.
 * @param selectedSignatureType Тип подписи (отсоединённая или присоединённая).
 * @param modalRef Ссылка на компонент модального окна для его закрытия по завершении процесса.
 * @param selectedFileExtension Расширение файла для подписи (опционально).
 * @param onStart Функция, которая вызывается перед началом процесса подписания.
 * @param onFinish Функция, которая вызывается после завершения процесса подписания.
 * @param handleSignedFileUpload Функция для отправки подписанного файла. По умолчанию использует sendSignedFile.
 */
export const processDocuments = async ({
    documentIdsToSign,
    attachmentIdsToSign,
    entityName,
    certificateThumbprint,
    certificateCadescomAlgorithm,
    selectedCertificate,
    selectedSignatureType,
    selectedFileExtension,
    selectedFileEncoding,
    modalRef,
    onStart,
    onFinish,
    handleSignedFileUpload = sendSignedFile,
}: IProcessDocumentsArgs): Promise<void> => {
    const idsToSign = (documentIdsToSign || attachmentIdsToSign) ?? [];
    const documentAmountToSign = idsToSign?.length;

    if (!documentAmountToSign) {
        console.error('Не переданы id на подпись');
    }

    onStart?.();

    notify({
        key: SIGNING_MESSAGE_KEY,
        documentCount: documentAmountToSign,
        status: 'loading',
    });

    modalRef.current?.closeModal();

    try {
        let hashesMap: Map<string, string> | undefined;

        if (attachmentIdsToSign) {
            const hashes = await fetchFilesHashes({
                entityName,
                algorithmCode: certificateCadescomAlgorithm,
                attachIds: attachmentIdsToSign,
            });
            hashesMap = new Map(hashes.map(({attachId, hash}) => [attachId, hash]));
        }

        const results = await Promise.all(
            idsToSign.map(async (id, index) => {
                try {
                    const {signatureFile, fileName, publishAllowed} = await signContent({
                        ...(documentIdsToSign && {docId: id}),
                        ...(attachmentIdsToSign && {attachId: attachmentIdsToSign[index]}),
                        entityName,
                        certificateCadescomAlgorithm,
                        certificateThumbprint,
                        selectedCertificate,
                        selectedSignatureType,
                        selectedFileExtension,
                        selectedFileEncoding,
                        precomputedFileHash: attachmentIdsToSign && hashesMap?.get(attachmentIdsToSign[index]),
                    });

                    await handleSignedFileUpload({
                        signatureFile,
                        fileName,
                        docId: id, /* TODO: если не передавать кастомный handleSignedFileUpload,
                        то в sendSignedFile будет уходить id = attachId, хотя ожидаем docId
                         */
                        publishAllowed,
                        entityName,
                        selectedFileExtension,
                    });

                    return {success: true};
                } catch (error) {
                    console.error(`Ошибка подписи документа ${id}:`, error);
                    return {success: false};
                }
            }),
        );

        const successfulCount = results.filter(({success}) => success).length;

        notify({
            key: SIGNING_MESSAGE_KEY,
            successfulCount,
            totalCount: documentAmountToSign,
            status: successfulCount === 0 ? 'error' : 'success',
        });

        onFinish?.();
    } catch (error) {
        console.error('Общая ошибка подписания:', error);
        notify({
            key: SIGNING_MESSAGE_KEY,
            status: 'error',
        });
    }
};
